import { useEffect, useState } from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import OrganizationChart from 'components/IncidentConfig/OrganizationChart';
import RosterActions from 'components/IncidentConfig/roster/RosterActions';
import RosterTable from 'components/IncidentConfig/roster/RosterTable';
import type { RosterViewMode } from 'components/IncidentConfig/roster/types';

import { useCreateRosterDraft } from './CreateRosterDraftContext';

export function DraftRosterWorkspace({
  hideTemplateSidebar = false,
  fullScreen = false,
  onFullScreenChange,
}: {
  hideTemplateSidebar?: boolean;
  fullScreen?: boolean;
  /** Offers the full-screen toggle when set. */
  onFullScreenChange?: (next: boolean) => void;
}) {
  const { members, removeMember } = useCreateRosterDraft();
  const [viewMode, setViewMode] = useState<RosterViewMode>('orgchart');

  useEffect(() => {
    if (hideTemplateSidebar && viewMode === 'analytics') {
      setViewMode('orgchart');
    }
  }, [hideTemplateSidebar, viewMode]);

  return (
    <div className="tw:flex tw:h-full tw:min-h-0 tw:flex-1 tw:flex-col">
      <RosterActions
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        hideAnalytics={hideTemplateSidebar}
        toolbarExtra={
          onFullScreenChange ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onFullScreenChange(!fullScreen)}
              aria-pressed={fullScreen}
            >
              {fullScreen ? (
                <Minimize2 className="tw:h-4 tw:w-4" />
              ) : (
                <Maximize2 className="tw:h-4 tw:w-4" />
              )}
              {fullScreen ? 'Exit Full Screen' : 'Full Screen'}
            </Button>
          ) : null
        }
      />

      {viewMode === 'table' ? (
        <div className="tw:min-h-0 tw:flex-1 tw:overflow-auto">
          <RosterTable
            rosterData={members}
            isRosterLoading={false}
            editMemberButtonClicked={() => undefined}
            removeMemberButtonClicked={(member) => removeMember(member.userId)}
            removeSelfFromIncidentButtonClicked={(member) =>
              removeMember(member.userId)
            }
            onDeleteSchedule={() => undefined}
            onToggleButtonClick={() => undefined}
            simplified
            showApprovalAuthority={false}
          />
        </div>
      ) : (
        <div className="tw:min-h-0 tw:flex-1">
          <OrganizationChart
            incidentMembers={members}
            hideTemplateSidebar={hideTemplateSidebar}
            chartHeightClassName={
              fullScreen ? 'tw:h-[calc(100vh-7rem)]' : undefined
            }
          />
        </div>
      )}
    </div>
  );
}

export default DraftRosterWorkspace;
